import posthog from 'posthog-js';
import { SESSION_RECORDING_OPTIONS } from '@/helpers/sessionRecording';
import { isLocalAnalyticsHost } from '@/helpers/isLocalAnalyticsHost';

/**
 * HAT-1081: PostHog לא מאותחל בעליית האפליקציה אלא רק אחרי הסכמת המשתמש.
 *
 * ‏opt_out_capturing_by_default לבדו לא הספיק: גם כשהוא כבוי מלכידה, ה-SDK
 * המאותחל פונה ל-eu.i.posthog.com (‏/decide, config.js, exception-autocapture)
 * ובכך מוסר את כתובת ה-IP של המשתמש לפני שנתן הסכמה. לכן אנחנו דוחים את
 * ה-init עצמו, ומעבירים ל-PostHogProvider את ה-singleton הלא-מאותחל -
 * קריאות capture/identify לפני האתחול הן no-op.
 */
let initialized = false;

/** מסיר querystring ו-hash מכתובת, ומשאיר את הנתיב בלבד. */
const stripQueryString = (url: string): string => url.split(/[?#]/)[0];

/** נקרא רק אחרי הסכמה. אידמפוטנטי - קריאה חוזרת (StrictMode) לא מאתחלת פעמיים. */
export const startPostHog = (): void => {
  if (typeof window !== 'undefined' && isLocalAnalyticsHost(window.location.hostname)) return;
  if (import.meta.env.DEV) return;
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (initialized || !apiKey) return;
  initialized = true;

  posthog.init(apiKey, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    capture_exceptions: true,
    // נוסף ב-master; נשמר כאן - מדד ביצועים ואינו מזהה.
    capture_performance: true,
    // HAT-1081: 'identified_only' במקום 'always' - מבקר אנונימי בעמודי התרומה
    // והקרן הציבוריים לא מקבל פרופיל אישי ב-PostHog.
    person_profiles: 'identified_only',
    // HAT-1081: autocapture נשאר פעיל (הוא מה שמאפשר לראות שימוש), אבל בלי
    // תוכן: טקסט של אלמנט שנלחץ - שם תורם בשורת טבלה, למשל - נשלח כמאפיין
    // אירוע, וזה מסלול שמיסוך ההקלטות (maskTextSelector) אינו מכסה כלל.
    mask_all_text: true,
    mask_all_element_attributes: true,
    debug: import.meta.env.MODE === 'development',
    // Super property: attaches the site to every event, so reports can be
    // filtered per client despite the shared PostHog project key.
    // (posthog.group() requires the paid Group Analytics add-on.)
    loaded: (ph) => {
      ph.register({ site: window.location.host, surface: 'new' });
      const params = new URLSearchParams(window.location.search);
      if (params.get('from') === 'old') {
        ph.capture('arrived_from_old_site', { landing_path: window.location.pathname });
      }
    },
    before_send: (event) => {
      if (!event) return event;
      if (event.event === '$exception') {
        const blob = JSON.stringify(event.properties ?? {});
        if (/ResizeObserver loop/i.test(blob)) return null;
      }
      // HAT-1081: ה-querystring יכול לשאת PII (למשל login-as?email=...)
      // ואסור שיגיע ל-PostHog - לא בכתובת הנוכחית ולא במפנה.
      if (event.properties) {
        for (const key of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']) {
          const value = event.properties[key];
          if (typeof value === 'string') {
            event.properties[key] = stripQueryString(value);
          }
        }
      }
      return event;
    },
    session_recording: SESSION_RECORDING_OPTIONS,
  });
};

export const isPostHogStarted = (): boolean => initialized;

export default posthog;
